import { useState } from "react";
import { motion } from "framer-motion";
import { ExternalLink, Filter } from "lucide-react";
import { SERVICES, type ServiceEntry } from "../data/services";

const CATEGORIES = ["all", "edge", "ai", "data", "automation", "observability", "auth", "compute"] as const;

const STATUS_STYLE: Record<string, { dot: string; pill: string; label: string }> = {
  healthy:  { dot: "bg-accent-emerald",  pill: "bg-accent-emerald/10 text-accent-emerald border-accent-emerald/20", label: "HEALTHY" },
  degraded: { dot: "bg-accent-amber",    pill: "bg-accent-amber/10 text-accent-amber border-accent-amber/20",     label: "DEGRADED" },
  broken:   { dot: "bg-accent-rose",     pill: "bg-accent-rose/10 text-accent-rose border-accent-rose/20",         label: "BROKEN" },
  idle:     { dot: "bg-slate-400",       pill: "bg-slate-400/10 text-slate-400 border-slate-400/20",               label: "IDLE" },
  unknown:  { dot: "bg-slate-600",       pill: "bg-slate-600/10 text-slate-500 border-slate-600/20",               label: "UNKNOWN" },
};

export default function Services() {
  const [cat, setCat] = useState<typeof CATEGORIES[number]>("all");
  const filtered = cat === "all" ? SERVICES : SERVICES.filter((s) => s.category === cat);
  return (
    <div className="space-y-6">
      <header>
        <div className="eyebrow">Layer · Edge & Compute</div>
        <h1 className="text-3xl font-bold mt-1 tracking-tight">Services</h1>
        <p className="text-slate-400 mt-2 max-w-2xl">
          Every deployed service across Cloudflare, Azure, Railway, Supabase, OpenAI, and the local fleet.
          Click any to open in a new tab.
        </p>
      </header>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 text-xs text-slate-500">
          <Filter size={12} /> Filter:
        </div>
        {CATEGORIES.map((c) => (
          <button
            key={c}
            onClick={() => setCat(c)}
            className={`px-3 py-1.5 rounded-full text-xs font-mono uppercase tracking-wider border transition ${
              cat === c
                ? "bg-white/[0.08] border-white/[0.14] text-white"
                : "bg-white/[0.02] border-white/[0.04] text-slate-400 hover:bg-white/[0.04]"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((s, i) => (
          <ServiceCard key={s.id} svc={s} delay={i * 0.03} />
        ))}
      </div>
    </div>
  );
}

function ServiceCard({ svc, delay }: { svc: ServiceEntry; delay: number }) {
  const st = STATUS_STYLE[svc.status];
  const isExternal = svc.url.startsWith("http");
  return (
    <motion.a
      href={svc.url}
      target={isExternal ? "_blank" : undefined}
      rel={isExternal ? "noreferrer" : undefined}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className="glass glass-hover p-5 block group relative overflow-hidden"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-white/[0.04] flex items-center justify-center text-accent-blue">
          <svc.icon size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold truncate">{svc.name}</h3>
            {isExternal && <ExternalLink size={11} className="text-slate-500 group-hover:text-accent-blue transition-colors" />}
          </div>
          <div className="eyebrow mt-0.5">{svc.platform}</div>
        </div>
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-mono border ${st.pill}`}>
          <span className={`inline-block w-1.5 h-1.5 rounded-full ${st.dot} mr-1.5 align-middle`} />
          {st.label}
        </span>
      </div>
      <p className="mt-3 text-xs text-slate-400 leading-relaxed">{svc.description}</p>
      {svc.notes && (
        <div className="mt-2 px-2.5 py-1.5 rounded bg-accent-amber/[0.06] border border-accent-amber/[0.15] text-[11px] text-accent-amber/90">
          {svc.notes}
        </div>
      )}
      <div className="mt-3 flex items-center justify-between text-[11px] font-mono text-slate-500">
        <span className="truncate">{svc.url.replace(/^https?:\/\//, "")}</span>
        {svc.cost && <span className="text-slate-400">{svc.cost}</span>}
      </div>
    </motion.a>
  );
}
